import { useTranslation } from 'react-i18next';
import { useSuspenseQuery } from '@tanstack/react-query';
import useUserDetails from '@/shared/data/useUserDetails';

const validImageUrl = (url: string) => {
  const img = new Image();
  img.src = url;

  return new Promise<boolean>((resolve) => {
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
  });
};

const useSrmImage = (url: string) =>
  useSuspenseQuery({
    queryKey: ['srm-image', url],
    queryFn: () => validImageUrl(url),
  });

const fallbackImage =
  'https://c2fo-static.s3-us-west-2.amazonaws.com/app/srm-images/srm-transparent/customerservice@c2fo.com.png';

const useSrm = () => {
  const { t } = useTranslation();
  const { data: srm } = useUserDetails((userDetails) => {
    const srm = userDetails.me?.account?.srm;
    return srm && srm.emailAddress
      ? {
          calendarLink: srm?.calendarLink ?? null,
          emailAddress: srm.emailAddress,
          image: `https://c2fo-static.s3-us-west-2.amazonaws.com/app/srm-images/srm-transparent/${srm.emailAddress}.png`,
          name: srm?.firstName && srm?.lastName ? `${srm.firstName} ${srm.lastName}` : null,
          phoneNumber: srm?.phoneNumber ? srm.phoneNumber : null,
          team: srm?.team,
        }
      : {
          // Setting calendarLink to null will hide the schedule meeting option in the SrmCard component
          calendarLink: null,
          emailAddress: 'support-amer@c2fo.com',
          image: fallbackImage,
          name: t('srm.accountSupport'),
          phoneNumber: '(866) 463-6565',
          team: 'USA-MKT',
        };
  });

  const { data: validSrmImageUrl = false } = useSrmImage(srm?.image ?? fallbackImage);

  const image = validSrmImageUrl && srm?.image ? srm.image : fallbackImage;

  return { ...srm, image };
};

export default useSrm;
